// Which modules show a team (teams and matches, Ben 2026-10-30: one module, player or team). The
// Bio and Matches show one themselves; League Table, Squad and Match Results open on it. Each
// module joins as it learns teams. A team drag is blue only over these.

import { teamOfModule, type TeamRef } from "../live/team.js";
import { getModuleConfigSync } from "../modules/module-config.js";
import { getModState, setModState } from "../modules/bridge.js";
import { canvasApi } from "./shell-actions.js";
import { getStickyTeam, setStickyTeam } from "./sticky-team.js";

/** What each module keeps when a team lands on it (its own `_`-state). The Bio shows the team
 *  itself; the other three were already about teams and open on the dropped one. */
/** A map showing a team: its own events first, every player ticked, no match. */
function teamMapState(team: TeamRef): Record<string, unknown> {
  return { _team: team, _teamShotSide: undefined, _mapMatch: undefined, _mapFromPlayer: undefined, _mapHidden: undefined, _mapPlayer: undefined };
}

const TEAM_DROPS: Readonly<Record<string, (team: TeamRef) => Record<string, unknown>>> = {
  renderBioModule: (team) => ({ _bioView: undefined, _team: team }),
  // Its numbers among its league's teams, its own (For) first.
  // A new team starts on its own league and season (its chips untouched).
  renderStatsModule: (team) => ({
    _team: team,
    _teamStatsSide: undefined,
    _teamStatsSeasonChip: undefined,
    _teamStatsLeagues: undefined,
    _mergedTeams: undefined,
    _mergedTeamColors: undefined,
  }),
  // Its Attacking radar among its league's teams; teams compared before go.
  renderRadarModule: (team) => ({
    _team: team,
    _teamRadarTemplate: undefined,
    _mergedTeams: undefined,
    _mergedTeamColors: undefined,
  }),
  // Its season's shots, its own first; any match it was narrowed to goes.
  renderShotMapModule: (team) => teamMapState(team),
  // Its season's passes, the same way (Ben, 2026-11-02), opening on zones (EventMapBody: a team's
  // season's default) with none picked.
  renderPassMapModule: (team) => ({ ...teamMapState(team), _passZones: undefined, _passZoneSelection: undefined }),
  // Its latest game (MatchModule finds it).
  renderMatchModule: (team) => ({ _match: undefined, _matchFor: { team } }),
  // Its games, in the season it was picked up in (else the newest), every game lit.
  renderMatchesModule: (team) => ({ _team: team, _teamMatchesSeason: team.season ?? undefined, _teamMatchesVenue: undefined }),
  // Its league and season, its row highlighted.
  renderLeagueTableModule: (team) => ({
    _leagueTableCompetition: team.competitionId,
    _leagueTableSeason: team.season ?? undefined,
    _leagueTableHighlight: team.id,
  }),
  // Its squad (the league's newest season, as a Squad always shows).
  renderSquadsModule: (team) => ({ _squadsCompetition: team.competitionId, _squadsTeam: team.id }),
  // Its results: the module's own team filter (by name, as its picker holds it).
  renderMatchResultsModule: (team) => ({ _matchResultsCompetition: team.competitionId, _matchResultsTeam: team.name }),
};

function rendererOf(mod: Element | null): string | undefined {
  if (!mod) return undefined;
  const title = mod.querySelector(".module-title")?.textContent?.trim() ?? "";
  return getModuleConfigSync(title)?.renderer;
}

/** Does this placed module take a team dropped on it? */
export function moduleTakesTeams(mod: Element | null): boolean {
  const renderer = rendererOf(mod);
  return renderer !== undefined && renderer in TEAM_DROPS;
}

/** The modules that show a team themselves, so their search boxes list teams too (the others
 *  pick a team with their own menus). */
const TEAM_SEARCH: ReadonlySet<string> = new Set([
  "renderBioModule",
  "renderMatchesModule",
  "renderStatsModule",
  "renderShotMapModule",
  "renderPassMapModule",
  "renderRadarModule",
]);

/** Does its search box list teams? */
export function moduleShowsTeams(mod: Element | null): boolean {
  const renderer = rendererOf(mod);
  return renderer !== undefined && TEAM_SEARCH.has(renderer);
}

/** Does this placed module take a match dropped on it? The Match module. */
export function moduleTakesMatches(mod: Element | null): boolean {
  return rendererOf(mod) === "renderMatchModule";
}

/** The `_`-state a team dropped on a module with this renderer sets (undefined = cleared). */
export function teamDropState(renderer: string, team: TeamRef): Record<string, unknown> {
  const drop = TEAM_DROPS[renderer];
  if (!drop) throw new Error(`${renderer} doesn't take a team`);
  // A linked player pick it was told to follow is overridden by the team.
  return { ...drop(team), _followPlayer: undefined };
}

/** The renderer of a module that takes teams (a drop target), or throws. */
export function teamDropRenderer(mod: Element): string {
  const renderer = rendererOf(mod);
  if (renderer === undefined || !(renderer in TEAM_DROPS)) throw new Error("This module doesn't take a team");
  return renderer;
}

/** Show `team` on a module that takes one. Holds no Undo step: the caller does. */
export function applyTeamDrop(target: HTMLElement, team: TeamRef): void {
  setStickyTeam(team);
  // The Bio shows the team (its card first, not the player's Career); a League Table, Squad or
  // Match Results opens on it.
  for (const [k, v] of Object.entries(teamDropState(teamDropRenderer(target), team))) setModState(target, k, v);
  canvasApi().refreshModuleBody(target);
  // A Bio's search box names what it shows (it reads `_team`; the player didn't change, so its
  // own update doesn't run).
  const box = target.querySelector<HTMLInputElement>(".player-search");
  if (box) box.value = team.name;
}

/** The team a whole module hands on when dropped: a Squad's (SquadModule keeps it as
 *  `_squadTeamRef`) or a Bio's team card (Ben, 2026-11-02: a team Bio dragged onto a Radar "should"
 *  add them, as onto any module that takes a team). Null for anything else. */
export function heldTeamOf(mod: HTMLElement): TeamRef | null {
  const renderer = rendererOf(mod);
  if (renderer === "renderSquadsModule") return getModState<TeamRef>(mod, "_squadTeamRef") ?? null;
  if (renderer === "renderBioModule") return teamOfModule(mod);
  return null;
}

/** A whole Squad or team Bio dropped on another module (Ben, 2026-10-31: the club handle went;
 *  "keep the squad"): a module that takes a team shows (or, a Stats tile or Radar showing teams,
 *  adds) that team, and the Squad or Bio stays where it was. */
export function heldTeamDrop(source: HTMLElement, target: HTMLElement): "show-team" | null {
  if (source === target || !heldTeamOf(source)) return null;
  return moduleTakesTeams(target) ? "show-team" : null;
}

/** The modules a brand-new copy of opens on the sticky team, when a team was picked last: the ones
 *  about teams. A player's maps and stat tiles keep opening on the player. */
const STICKY_TEAM_RENDERERS: ReadonlySet<string> = new Set([
  "renderBioModule",
  "renderMatchesModule",
  "renderMatchModule",
  "renderLeagueTableModule",
  "renderSquadsModule",
  "renderMatchResultsModule",
]);

/** The `_`-state a new module titled `title` opens with when a team was picked last, or null. */
export function stickyTeamState(title: string): Record<string, unknown> | null {
  const team = getStickyTeam();
  const cfg = getModuleConfigSync(title);
  if (!team || !cfg) return null;
  // The team-only Stats tiles (Possession, Set Pieces, Discipline) too.
  const teamOnlyStats = cfg.renderer === "renderStatsModule" && cfg.needsPlayer === false;
  if (!STICKY_TEAM_RENDERERS.has(cfg.renderer) && !teamOnlyStats) return null;
  return teamDropState(cfg.renderer, team);
}
